import apiRequest from "src/utils/api-request";
import withApiPath from "src/utils/with-api-path";

export interface RevenueOverview {
	orderRevenueUsd: number;
	withdrawalRevenueUsd: number;
	totalRevenueUsd: number;
	amlCheckCount: number;
	amlCostUsd: number;
	energyRentalCount: number;
	energyCostUsd: number;
	totalCostUsd: number;
	netProfitUsd: number;
	profitMargin: number;
}

export interface BlockchainBreakdown {
	blockchain: string;
	orderRevenueUsd: number;
	withdrawalRevenueUsd: number;
	energyCostUsd: number;
	netRevenueUsd: number;
	orderCount: number;
	withdrawalCount: number;
	volumeUsd: number;
}

export interface CurrencyBreakdown {
	currency: string;
	revenueUsd: number;
	orderCount: number;
	volumeUsd: number;
}

export interface MerchantBreakdown {
	merchantId: number;
	merchantName: string;
	userEmail: string;
	orderRevenueUsd: number;
	withdrawalRevenueUsd: number;
	totalRevenueUsd: number;
	orderCount: number;
	volumeUsd: number;
}

export interface CostBreakdown {
	amlCheckCount: number;
	amlCostUsd: number;
	energyInternalCount: number;
	energyInternalCostUsd: number;
	energyInternalCostTrx: number;
	energyMerchantWithdrawalCount: number;
	energyMerchantWithdrawalCostUsd: number;
	energyMerchantWithdrawalCostTrx: number;
	energyPoolWithdrawalCount: number;
	energyPoolWithdrawalCostUsd: number;
	energyPoolWithdrawalCostTrx: number;
	totalEnergyCostUsd: number;
	totalEnergyCostTrx: number;
	totalCostUsd: number;
}

export interface WalletBalance {
	currency: string;
	amount: number;
	decimals: number;
}

export interface OutboundWallet {
	walletId: number;
	walletUuid: string;
	address: string;
	blockchain: string;
	balances: WalletBalance[];
	totalUsd: number;
}

export interface WalletBalances {
	wallets: OutboundWallet[];
	totalUsd: number;
}

export interface DailyDataPoint {
	date: string;
	orderRevenueUsd: number;
	withdrawalRevenueUsd: number;
	volumeUsd: number;
	orderCount: number;
	withdrawalCount: number;
	amlCount: number;
	amlCostUsd: number;
	energyCount: number;
	energyCostUsd: number;
}

export interface TimeSeries {
	startDate: string;
	endDate: string;
	data: DailyDataPoint[];
}

const revenueProvider = {
	async getOverview(
		startDate?: string,
		endDate?: string,
	): Promise<RevenueOverview> {
		const params: Record<string, string> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;

		const response = await apiRequest.get(withApiPath("/revenue/overview"), {
			params,
		});
		return response.data;
	},

	async getBlockchainBreakdown(
		startDate?: string,
		endDate?: string,
	): Promise<BlockchainBreakdown[]> {
		const params: Record<string, string> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;

		const response = await apiRequest.get(
			withApiPath("/revenue/breakdown/blockchain"),
			{ params },
		);
		return response.data;
	},

	async getCurrencyBreakdown(
		startDate?: string,
		endDate?: string,
	): Promise<CurrencyBreakdown[]> {
		const params: Record<string, string> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;

		const response = await apiRequest.get(
			withApiPath("/revenue/breakdown/currency"),
			{ params },
		);
		return response.data;
	},

	async getMerchantBreakdown(
		startDate?: string,
		endDate?: string,
		limit?: number,
		offset?: number,
	): Promise<MerchantBreakdown[]> {
		const params: Record<string, string | number> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;
		if (limit !== undefined) params.limit = limit;
		if (offset !== undefined) params.offset = offset;

		const response = await apiRequest.get(
			withApiPath("/revenue/breakdown/merchant"),
			{ params },
		);
		return response.data;
	},

	async getCostBreakdown(
		startDate?: string,
		endDate?: string,
	): Promise<CostBreakdown> {
		const params: Record<string, string> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;

		const response = await apiRequest.get(withApiPath("/revenue/costs"), {
			params,
		});
		return response.data;
	},

	async getWalletBalances(): Promise<WalletBalances> {
		const response = await apiRequest.get(withApiPath("/revenue/wallets"));
		return response.data;
	},

	async getTimeSeries(
		startDate?: string,
		endDate?: string,
	): Promise<TimeSeries> {
		const params: Record<string, string> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;

		const response = await apiRequest.get(withApiPath("/revenue/timeseries"), {
			params,
		});
		return response.data;
	},

	async exportCSV(
		startDate?: string,
		endDate?: string,
	): Promise<{ data: Blob; filename: string }> {
		const params: Record<string, string> = {};
		if (startDate) params.start_date = startDate;
		if (endDate) params.end_date = endDate;

		const response = await apiRequest.get(withApiPath("/revenue/export"), {
			params,
			responseType: "blob",
		});

		// Extract filename from Content-Disposition header if available
		const contentDisposition = response.headers["content-disposition"];
		let filename = `revenue_${startDate}_${endDate}.csv`;
		if (contentDisposition) {
			const filenameMatch = contentDisposition.match(/filename=([^;]+)/);
			if (filenameMatch) {
				filename = filenameMatch[1].trim();
			}
		}

		return { data: response.data, filename };
	},
};

export default revenueProvider;
